import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { photoUrl } from "@/lib/api";
import {
  Bot,
  Send,
  Sparkles,
  FileText,
  ExternalLink,
  Package,
  Loader2,
  Search as SearchIcon,
  ArrowRight,
  User,
  X,
  Building2,
  Plus,
  Check,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { formatApiError } from "@/lib/errors";
import { useAgency } from "@/context/AgencyContext";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import StreamlinedSearchFlow from "@/components/StreamlinedSearchFlow";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GREETING = "What can we help you find today?\n\nTry one of these to get started, or just describe what you need in your own words. Our product database is growing — new manufacturers and SKUs are added regularly.";
const STARTER_CHIPS = [
  "Exhaust fans",
  "Flat panels",
  "Exit signs",
  "Wire connectors",
];

/**
 * Phase 3 — Streamlined search (default), with the legacy AI wizard
 * available as a fallback for any manufacturer flagged search_mode="guided"
 * or via the "Try AI search" toggle.
 */
export default function SearchPage() {
  const { user } = useAuth();
  const { active: switcherAgency, activeId: switcherAgencyId } = useAgency();
  const isDistributor = user?.role === "distributor";
  const activeId = isDistributor ? user?.agency_id : switcherAgencyId;
  const activeAgency = useMemo(() => {
    if (!isDistributor) return switcherAgency;
    if (!user?.agency_id) return null;
    return { id: user.agency_id, name: user.agency_name || "your rep agency" };
  }, [isDistributor, switcherAgency, user?.agency_id, user?.agency_name]);

  // Default mode is streamlined; switches to wizard when a user picks a
  // manufacturer that's flagged search_mode="guided".
  const [mode, setMode] = useState("streamlined");
  const [guidedMfr, setGuidedMfr] = useState(null);
  // Incremented to force StreamlinedSearchFlow to fully remount on "Start over".
  const [resetKey, setResetKey] = useState(0);
  const pricingEmails = activeAgency?.pricing_emails || [];

  const goWizard = (mfr) => {
    setGuidedMfr(mfr || null);
    setMode("wizard");
  };
  const goStreamlined = () => {
    setGuidedMfr(null);
    setMode("streamlined");
  };
  const startOver = () => {
    setGuidedMfr(null);
    setMode("streamlined");
    setResetKey((k) => k + 1);
  };

  return (
    <div className="max-w-5xl mx-auto" data-testid="search-page">
      {/* Header */}
      <div className="mb-4 flex items-start justify-between flex-wrap gap-2">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-amber-600 font-mono-data flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" />
            Spec sheet finder
          </div>
          <h1 className="text-2xl font-display font-bold tracking-tight text-slate-900 mt-1">
            What can I help you find?
          </h1>
          {activeAgency && (
            <div
              className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] text-muted-foreground font-mono-data"
              data-testid="active-agency-banner"
            >
              <Building2 className="w-3 h-3 text-amber-600" />
              Searching{" "}
              <span className="font-semibold text-slate-700">{activeAgency.name}</span>{" "}
              portfolio
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {mode === "wizard" ? (
            <Button
              variant="outline"
              size="sm"
              onClick={goStreamlined}
              className="rounded-sm"
              data-testid="wizard-back-to-streamlined"
            >
              <X className="w-3.5 h-3.5 mr-1.5" /> Back to manufacturers
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={startOver}
              className="rounded-sm"
              data-testid="start-over-btn"
            >
              <X className="w-3.5 h-3.5 mr-1.5" /> Start over
            </Button>
          )}
        </div>
      </div>

      {mode === "streamlined" ? (
        <StreamlinedSearchFlow
          key={resetKey}
          activeAgency={activeAgency}
          activeId={activeId}
          pricingEmails={pricingEmails}
          onUseGuidedWizard={goWizard}
        />
      ) : (
        <WizardChat
          activeAgency={activeAgency}
          activeId={activeId}
          initialPricingEmails={pricingEmails}
          guidedMfr={guidedMfr}
        />
      )}
    </div>
  );
}

function WizardChat({ activeAgency, activeId, initialPricingEmails, guidedMfr }) {
  // When the user entered the wizard from a manufacturer card, the starter
  // chips should reflect ONLY that manufacturer's product types — not the
  // hardcoded fallback list (which can show types from sibling manufacturers).
  const [starterChips, setStarterChips] = useState(STARTER_CHIPS);
  // history: [{ role: 'assistant' | 'user', content, chips?, results? }]
  const [messages, setMessages] = useState([
    { role: "assistant", content: GREETING, chips: STARTER_CHIPS, results: [] },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showKeywordFallback, setShowKeywordFallback] = useState(false);
  const [pricingEmails, setPricingEmails] = useState(initialPricingEmails || []);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    setPricingEmails(initialPricingEmails || []);
  }, [initialPricingEmails]);

  // Fetch this manufacturer's product types so the starter chips match what
  // they actually offer. Falls back to STARTER_CHIPS on error or when no
  // guided manufacturer is set.
  useEffect(() => {
    let cancelled = false;
    if (!guidedMfr?.id) {
      setStarterChips(STARTER_CHIPS);
      setMessages([
        { role: "assistant", content: GREETING, chips: STARTER_CHIPS, results: [] },
      ]);
      setShowKeywordFallback(false);
      return;
    }
    // Optimistically reset to the static fallback while we load
    setStarterChips(STARTER_CHIPS);
    setMessages([
      { role: "assistant", content: GREETING, chips: STARTER_CHIPS, results: [] },
    ]);
    setShowKeywordFallback(false);
    api
      .get(`/manufacturers/${guidedMfr.id}/product-types`)
      .then(({ data }) => {
        if (cancelled) return;
        const chips = (data || [])
          .map((pt) => (pt.display_name || pt.name || "").trim())
          .filter(Boolean);
        const uniq = Array.from(new Set(chips)).slice(0, 8);
        const next = uniq.length > 0 ? uniq : STARTER_CHIPS;
        setStarterChips(next);
        setMessages([
          { role: "assistant", content: GREETING, chips: next, results: [] },
        ]);
      })
      .catch(() => {
        // Quietly fall back; not worth toast-spamming the user.
      });
    return () => {
      cancelled = true;
    };
  }, [activeId, guidedMfr?.id]);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    const userMsg = { role: "user", content: msg };
    const nextHistory = [...messages, userMsg];
    setMessages(nextHistory);
    setLoading(true);
    try {
      // Send only role+content history (strip chips/results) — server expects flat dialogue
      const history = nextHistory
        .filter((m) => m.role === "user" || m.role === "assistant")
        .slice(0, -1) // exclude the latest user message — backend appends it
        .map((m) => ({ role: m.role, content: m.content }));
      const { data } = await api.post("/search/wizard", {
        message: msg,
        history,
        rep_agency_id: activeId || undefined,
        // Pin the wizard to the guided manufacturer so the AI's catalog
        // brief is scoped to just their products.
        manufacturer_id: guidedMfr?.id || undefined,
      });
      if (data.rep_agency?.pricing_emails) {
        setPricingEmails(data.rep_agency.pricing_emails);
      }
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: data.reply,
          chips: data.suggested_replies || [],
          results: data.results || [],
          stage: data.stage,
          filter: data.filter,
          pricingEmails: data.rep_agency?.pricing_emails || pricingEmails,
        },
      ]);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // Soft-fail: if AI is unavailable for any reason, offer the keyword fallback
      if (e?.response?.status === 402 || e?.response?.status === 429) {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content:
              (detail || "AI search is temporarily unavailable.") +
              " You can use keyword search below as a fallback.",
            chips: [],
            results: [],
          },
        ]);
        setShowKeywordFallback(true);
      } else {
        toast.error(formatApiError(e, "Wizard failed"));
      }
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const reset = () => {
    setMessages([
      { role: "assistant", content: GREETING, chips: starterChips, results: [] },
    ]);
    setInput("");
    setShowKeywordFallback(false);
    inputRef.current?.focus();
  };

  return (
    <div className="h-[calc(100vh-260px)] flex flex-col" data-testid="search-wizard-chat">
      {guidedMfr && (
        <div className="mb-3 text-[11px] uppercase tracking-widest text-amber-700 font-mono-data inline-flex items-center gap-1.5 bg-amber-50 border border-amber-200 px-2 py-1 rounded-sm w-fit">
          <Bot className="w-3 h-3" />
          Guided wizard for <span className="font-bold">{guidedMfr.name}</span>
        </div>
      )}

      {/* Chat messages */}
      <div className="flex-1 overflow-y-auto bg-slate-50 border border-border rounded-sm p-4 space-y-4">
        {messages.map((m, i) => (
          <ChatBubble
            key={i}
            message={m}
            onChipClick={(c) => send(c)}
            pricingEmails={m.pricingEmails || pricingEmails}
          />
        ))}
        {loading && (
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-sm bg-slate-900 text-white flex items-center justify-center shrink-0">
              <Bot className="w-4 h-4" />
            </div>
            <div className="bg-white border border-border rounded-sm px-3 py-2 text-sm text-muted-foreground inline-flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Thinking…
            </div>
          </div>
        )}
        <div ref={scrollRef} />
      </div>

      {/* Input */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
        className="mt-4 relative"
      >
        <Input
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Let us know which options you need"
          className="rounded-sm h-12 pr-14"
          disabled={loading}
          autoFocus
          data-testid="wizard-input"
        />
        <Button
          type="submit"
          disabled={loading || !input.trim()}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 h-9 w-9 p-0 rounded-sm bg-amber-500 hover:bg-amber-600 text-slate-900"
          data-testid="wizard-send-btn"
        >
          <Send className="w-4 h-4" />
        </Button>
      </form>

      <div className="mt-2 text-right">
        <button
          type="button"
          onClick={reset}
          className="text-[11px] text-muted-foreground hover:text-slate-900 font-mono-data uppercase tracking-wider"
          data-testid="wizard-reset-btn"
        >
          Start over
        </button>
      </div>

      {/* Keyword fallback */}
      {showKeywordFallback && <KeywordFallback pricingEmails={pricingEmails} repAgencyId={activeId} />}
    </div>
  );
}

function ChatBubble({ message, onChipClick, pricingEmails }) {
  const isUser = message.role === "user";
  return (
    <div className={`flex items-start gap-3 ${isUser ? "flex-row-reverse" : ""}`}>
      <div
        className={`w-8 h-8 rounded-sm flex items-center justify-center shrink-0 ${
          isUser ? "bg-amber-500 text-slate-900" : "bg-slate-900 text-white"
        }`}
      >
        {isUser ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
      </div>
      <div className={`flex-1 min-w-0 ${isUser ? "text-right" : ""}`}>
        <div
          className={`inline-block max-w-[85%] rounded-sm px-3 py-2 text-sm whitespace-pre-wrap ${
            isUser
              ? "bg-amber-500 text-slate-900 font-medium"
              : "bg-white border border-border text-slate-900"
          }`}
          data-testid={`chat-bubble-${message.role}`}
        >
          {message.content}
        </div>
        {!isUser && message.chips && message.chips.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {message.chips.map((c) => (
              <button
                key={c}
                onClick={() => onChipClick(c)}
                className="text-xs bg-white hover:bg-amber-50 border border-border hover:border-amber-300 text-slate-700 hover:text-amber-800 px-2.5 py-1 rounded-sm transition-colors"
                data-testid="wizard-chip"
              >
                {c}
              </button>
            ))}
          </div>
        )}
        {!isUser && message.results && message.results.length > 0 && (
          <div className="mt-3 space-y-2">
            <div className="text-[10px] uppercase tracking-widest text-amber-700 font-mono-data flex items-center gap-1">
              <Sparkles className="w-3 h-3" />
              {message.results.length} match{message.results.length === 1 ? "" : "es"}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {message.results.slice(0, 8).map((r) => (
                <ResultCardCompact key={r.id} result={r} />
              ))}
            </div>
            {message.results.length > 8 && (
              <div className="text-xs text-muted-foreground">
                Showing first 8 of {message.results.length}. Ask me to narrow further.
              </div>
            )}
            <PricingContactFooter emails={pricingEmails} />
          </div>
        )}
      </div>
    </div>
  );
}

function ResultCardCompact({ result }) {
  const { addItem, setDrawerOpen, cart } = useCart();
  const [adding, setAdding] = useState(false);
  const [justAdded, setJustAdded] = useState(false);
  const inCart = (cart.items || []).some((it) => it.product_id === result.id);

  const photo = result.primary_photo_id ? photoUrl(result.primary_photo_id) : null;
  const specUrl = result.spec_sheet_url;
  const attrs = result.attributes || {};
  const cols = result.product_type?.columns || [];
  const keyChips = cols
    .filter(
      (c) =>
        attrs[c.key] !== undefined &&
        attrs[c.key] !== null &&
        attrs[c.key] !== "" &&
        !(Array.isArray(attrs[c.key]) && attrs[c.key].length === 0)
    )
    .slice(0, 3);
  const fullSpecUrl = specUrl
    ? specUrl.startsWith("http")
      ? specUrl
      : `${BACKEND_URL}${specUrl}`
    : null;

  const handleAdd = async () => {
    setAdding(true);
    try {
      await addItem(result.id, { quantity: 1 });
      setJustAdded(true);
      toast.success(`Added "${result.name}" to your quote cart`, {
        action: { label: "View cart", onClick: () => setDrawerOpen(true) },
      });
      setTimeout(() => setJustAdded(false), 1800);
    } catch (err) {
      toast.error(formatApiError(err, "Could not add to cart"));
    } finally {
      setAdding(false);
    }
  };

  return (
    <div
      className="bg-white border border-border hover:border-amber-300 rounded-sm p-3 flex flex-col gap-2 transition-all"
      data-testid="search-result-card"
    >
      <div className="flex gap-2.5">
        <div className="w-16 h-16 bg-slate-100 rounded-sm shrink-0 flex items-center justify-center overflow-hidden">
          {photo ? (
            <img src={photo} alt={result.name} className="w-full h-full object-contain p-1" />
          ) : (
            <Package className="w-8 h-8 text-slate-400" strokeWidth={1.25} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono-data truncate">
            {result.manufacturer.name}
          </div>
          <div className="font-display text-sm font-bold tracking-tight text-slate-900 truncate">
            {result.name}
          </div>
          {result.model_number && (
            <div className="text-[11px] font-mono-data text-slate-700 truncate">
              {result.model_number}
            </div>
          )}
          {keyChips.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {keyChips.map(({ key, label }) => {
                const v = attrs[key];
                return (
                  <span
                    key={key}
                    className="text-[9px] bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded-sm font-mono-data"
                  >
                    {Array.isArray(v) ? v.join(",") : String(v)}
                  </span>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {fullSpecUrl ? (
          <a
            href={fullSpecUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold py-1.5 rounded-sm transition-colors"
            data-testid="result-spec-btn"
          >
            <FileText className="w-3.5 h-3.5" />
            Spec sheet
            <ExternalLink className="w-3 h-3" />
          </a>
        ) : (
          <span className="inline-flex items-center justify-center gap-1.5 bg-slate-100 text-muted-foreground text-xs py-1.5 rounded-sm italic">
            No spec sheet
          </span>
        )}
        <button
          type="button"
          onClick={handleAdd}
          disabled={adding}
          className={`inline-flex items-center justify-center gap-1.5 text-xs font-semibold py-1.5 rounded-sm transition-colors border ${
            justAdded || inCart
              ? "bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100"
              : "bg-amber-500 hover:bg-amber-600 border-amber-500 text-slate-900"
          }`}
          data-testid="result-add-to-cart-btn"
        >
          {adding ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : justAdded ? (
            <>
              <Check className="w-3.5 h-3.5" />
              Added
            </>
          ) : inCart ? (
            <>
              <Check className="w-3.5 h-3.5" />
              In cart · Add another
            </>
          ) : (
            <>
              <Plus className="w-3.5 h-3.5" />
              Add to cart
            </>
          )}
        </button>
      </div>
    </div>
  );
}

function PricingContactFooter({ emails }) {
  const list = emails && emails.length > 0 ? emails : [];
  if (list.length === 0) {
    return (
      <div
        className="mt-2 bg-slate-50 border border-border rounded-sm px-3 py-2 text-xs text-muted-foreground italic"
        data-testid="pricing-contact-footer"
      >
        Pricing contact not configured for this agency.
      </div>
    );
  }
  return (
    <div
      className="mt-2 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2 text-xs text-amber-900"
      data-testid="pricing-contact-footer"
    >
      Have questions? Email{" "}
      {list.map((e, i) => (
        <React.Fragment key={e}>
          <a
            href={`mailto:${e}`}
            className="font-semibold underline hover:text-amber-700"
            data-testid={`pricing-email-link-${e}`}
          >
            {e}
          </a>
          {i < list.length - 2 ? ", " : i === list.length - 2 ? " or " : ""}
        </React.Fragment>
      ))}
      .
    </div>
  );
}

/**
 * Keyword search fallback — used when the AI wizard is unavailable
 * (budget, rate limit, network). Hits the same `/api/search` endpoint
 * the wizard backend uses internally.
 */
function KeywordFallback({ pricingEmails, repAgencyId }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const run = async (text) => {
    if (!text.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.get("/search", {
        params: {
          q: text,
          limit: 20,
          ...(repAgencyId ? { rep_agency_id: repAgencyId } : {}),
        },
      });
      setResults(data.results || []);
    } catch (e) {
      toast.error(formatApiError(e, "Search failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-6 border-t border-border pt-4">
      <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data mb-2">
        Keyword search (AI fallback)
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          run(q);
        }}
        className="relative mb-3"
      >
        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search products"
          className="rounded-sm pl-9 h-10"
        />
      </form>
      {loading ? (
        <div className="text-sm text-muted-foreground">Searching…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {results.map((r) => (
              <ResultCardCompact key={r.id} result={r} />
            ))}
          </div>
          {results.length > 0 && <PricingContactFooter emails={pricingEmails} />}
        </>
      )}
    </div>
  );
}
